"use client";

import {
  AppHeroSection,
  AppFeaturesSection,
  AppPricingSection,
  AppFAQSection,
  AppGuidesSection,
  AppBannerShowcase,
} from "@/components/custom/shared";
import { productOptionsFaqs, productOptionsGuides } from "./data";

// Product Options specific data
const productOptionsFeatures = [
  {
    title: "Live Preview & Customization.",
    description:
      "Provide instant visual feedback with live previews. Adjust colors, fonts, and styles to match your brand.",
    image: "/images/PO-Feature-1.webp",
  },
  {
    title: "Unlimited Product & Variant Options.",
    description:
      "Add product options like dropdowns, swatches, radio buttons, and more. Ideal for stores with personalization, size, or material choices.",
    image: "/images/PO-Feature-2.webp",
  },
  {
    title: "Conditional Logic.",
    description:
      'Show options at the right time. Use simple "if-then" rules to display specific options when relevant, like custom engraving fields when "Personalized" is selected.',
    image: "/images/PO-Feature-3.webp",
  },
];

const productOptionsPricingPlans = [
  {
    name: "Free",
    description: "Get started with the basics — no credit card needed.",
    price: 0,
    ctaText: "Install free",
    ctaLink: "https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=freeplan",
    features: [
      "10 Option Sets",
      "12 Option Types",
      "Basic Styling Customization",
      "Email Support",
    ],
  },
  {
    name: "Essentials",
    description: "For growing stores that need more flexibility.",
    price: 9.99,
    annualPrice: 8.25,
    shopifyPlusPrice: 39.9,
    shopifyPlusAnnualPrice: 33.25,
    ctaText: "Start 14-day free trial",
    ctaLink: "https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=essentialsplan",
    includesLabel: "Everything in Free, plus:",
    features: [
      "Unlimited Option Sets",
      "Unlimited Option Types",
      "Conditional Logic",
      "Price Add-ons",
      "File Uploads",
      "24/7 Live Chat Support",
    ],
  },
  {
    name: "Premium",
    description: "For power sellers who need the full toolkit.",
    price: 29.9,
    annualPrice: 24.92,
    shopifyPlusPrice: 59.9,
    shopifyPlusAnnualPrice: 49.92,
    popular: true,
    ctaText: "Start 14-day free trial",
    ctaLink: "https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=premiumplan",
    includesLabel: "Everything in Essentials, plus:",
    features: [
      "Advanced Conditional Logic",
      "Inventory Tracking",
      "Option Templates",
      "Priority Support (<5-min SLA)",
    ],
  },
];


const ProductOptionsPage = () => {
  return (
    <>
      {/* Hero Section */}
      <AppHeroSection
        brand=""
        title="Keystone Product Options, Variants"
        description="Create unlimited product options and variants with live previews, custom fields, and conditional logic- all without code."
        ctaText="Install now"
        ctaLink="https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=productpageheader"
        iconSrc="/brands/product-options-square.svg"
        heroImage="/images/Product-Options-Hero-Card.png"
        heroImageTablet="/images/Product-Options-Hero-Card.png"
        heroImageMobile="/images/Product-Options-Hero-Card.png"
        backgroundSvg="/product-options-background.svg"
      />

      {/* Features Section */}
      <AppFeaturesSection
        title="Designed to convert. Built to scale."
        features={productOptionsFeatures}
        glowGradient="linear-gradient(318.5deg, #BBE1E6 20.26%, #FCFB00 54.99%, #87D500 79.02%)"
      />

      {/* Pricing Section */}
      <AppPricingSection
        id="pricing"
        title="Pricing"
        plans={productOptionsPricingPlans}
        accentColor="#A1F50F"
        popularBorderColor="#fcfb00"
        popularBadgeColor="#f8db4c"
        popularBadgeTextColor="black"
        popularButtonGlowColor="rgba(252,251,0,0.4)"
        popularBorderGradient="linear-gradient(90deg, #A5F40E 0%, #FEEA07 100%)"
        annualDiscountLabel="-17%"
      />

      {/* Banner Showcase */}
      <AppBannerShowcase
        title="Bring customization to life - give customers control over how they buy"
        description="Give shoppers the freedom to customize. Add swatches and personal touches to create a tailored experience that grows your sales naturally."
        ctaText="Install now"
        ctaLink="https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=productpagefooter"
        backgroundImage="/images/product-options-banner-bg.webp"
      />

      {/* FAQ Section */}
      <AppFAQSection title="FAQs" faqs={productOptionsFaqs} />

      <AppGuidesSection title="Product options guides" guides={productOptionsGuides} />
    </>
  );
};

export default ProductOptionsPage;
